import FPS from './fps'
import Bag from './bag'
import Terrain, { BlockType } from '../terrain'
import Block from '../terrain/mesh/block'
import Control from '../control'
import Player, { Mode, GameMode } from '../player'
import Joystick from './joystick'
import InventoryUI from './inventory-ui'
import BlockDropManager from '../drops'
import { isMobile } from '../utils'
import * as THREE from 'three'
import { InventoryItemType, ItemType } from '../items'

const PLAY_LABEL = 'Play'
const RESUME_LABEL = 'Resume'
const LOAD_LABEL = 'Load Game'
const SAVE_EXIT_LABEL = 'Save and Exit'
const PLAYER_STATS_STORAGE_KEY = 'playerStats'
const BASE_HUNGER_INTERVAL_SECONDS = 30
const CREATIVE_ITEMS: InventoryItemType[] = [
  BlockType.grass,
  BlockType.dirt,
  BlockType.stone,
  BlockType.sand,
  BlockType.tree,
  BlockType.wood,
  BlockType.leaf,
  BlockType.coal,
  BlockType.diamond,
  BlockType.quartz,
  BlockType.glass,
  BlockType.water,
  BlockType.craftingTable,
  BlockType.bedrock,
  BlockType.lava,
  BlockType.obsidian,
  BlockType.cobblestone,
  BlockType.tnt,
  BlockType.whitespaceGrass,
  BlockType.whitespaceDirt,
  BlockType.whitespaceLog,
  BlockType.whitespaceLeaf,
  BlockType.netherrack,
  BlockType.soulSand,
  BlockType.glowstone,
  BlockType.netherQuartz,
  BlockType.netherPortal,
  ItemType.stick,
  ItemType.woodenPickaxe,
  ItemType.stonePickaxe,
  ItemType.bucket,
  ItemType.waterBucket,
  ItemType.lavaBucket,
  ItemType.rawBeef,
  ItemType.cookedBeef,
  ItemType.rawPorkchop,
  ItemType.cookedPorkchop,
  ItemType.rawMutton,
  ItemType.cookedMutton,
  ItemType.rawChicken,
  ItemType.cookedChicken,
  ItemType.rawRabbit,
  ItemType.cookedRabbit,
  ItemType.egg,
  ItemType.feather,
  ItemType.leather,
  ItemType.wool,
  ItemType.rottenFlesh,
  ItemType.bone,
  ItemType.string,
  ItemType.spiderEye,
  ItemType.gunpowder
]

type PlayerStatsSave = {
  health?: number
  hunger?: number
  morale?: number
}

export default class UI {
  constructor(
    terrain: Terrain,
    control: Control,
    dropManager: BlockDropManager
  ) {
    this.fps = new FPS()
    this.bag = new Bag(control.player.inventory, {
      getCreativeItems: () =>
        control.player.gameMode === GameMode.creative
          ? control.holdingItems
          : null
    })
    this.joystick = new Joystick(control)
    this.terrain = terrain
    this.controlRef = control
    this.dropManager = dropManager
    this.inventoryUI = new InventoryUI(control.player.inventory, {
      getCreativeItems: () =>
        control.player.gameMode === GameMode.creative ? CREATIVE_ITEMS : null,
      getCreativeHotbarItems: () =>
        control.player.gameMode === GameMode.creative
          ? control.holdingItems
          : null,
      setCreativeHotbarItem: (index, type) => {
        control.holdingItems[index] = type
        control.holdingBlock = control.getHoldingBlockType()
      }
    })

    this.crossHair.className = 'cross-hair'
    this.crossHair.textContent = '+'
    this.crossHair.classList.add('hidden')
    document.body.appendChild(this.crossHair)
    this.statusChip.className = 'game-status hidden'
    document.body.appendChild(this.statusChip)
    this.moraleVignette.className = 'morale-vignette hidden'
    document.body.appendChild(this.moraleVignette)
    this.fireVignette.className = 'fire-vignette hidden'
    document.body.appendChild(this.fireVignette)
    this.underwaterVignette.className = 'underwater-vignette hidden'
    document.body.appendChild(this.underwaterVignette)
    this.portalVignette.className = 'portal-vignette hidden'
    document.body.appendChild(this.portalVignette)
    this.miningBar.className = 'mining-progress hidden'
    this.miningFill.className = 'mining-progress-fill'
    this.miningBar.appendChild(this.miningFill)
    document.body.appendChild(this.miningBar)
    this.bag.bag.classList.add('hidden')
    this.fps.fps.classList.add('hidden')

    // game mode selector
    this.modeSurvival?.addEventListener('click', () => {
      control.player.setGameMode(GameMode.survival)
      this.modeSurvival?.classList.add('mode-active')
      this.modeCreative?.classList.remove('mode-active')
    })
    this.modeCreative?.addEventListener('click', () => {
      control.player.setGameMode(GameMode.creative)
      this.modeCreative?.classList.add('mode-active')
      this.modeSurvival?.classList.remove('mode-active')
    })

    // respawn
    this.respawnBtn?.addEventListener('click', () => {
      control.player.respawn()
      // respawn at world spawn — respawning in place can drop you straight
      // back into the lava or cavity that killed you
      terrain.camera.position.x = 8
      terrain.camera.position.z = 8
      terrain.camera.position.y = terrain.getElevation(8, 8) + 4
      this.deathScreen?.classList.add('hidden')
      this.survivalHud?.classList.remove('hidden')
      this.crossHair.classList.remove('hidden')
      this.updateHealthBar(control.player)
      this.updateHungerBar(control.player)
      this.syncAirBar(control.player)
      control.setActive(true)
      document.body.classList.add('game-cursor-hidden')
      this.lockPointer()
    })

    // damage flash callback
    control.onDamage = () => {
      this.showDamageFlash()
    }
    control.onOpenCraftingTable = () => {
      if (this.isTitleScreen || this.inventoryUI.isOpen) return
      this.inventoryUI.open('table')
      control.setActive(false)
      document.body.classList.remove('game-cursor-hidden')
      this.unlockPointer()
    }
    control.onMiningProgress = progress => {
      this.updateMiningProgress(progress)
    }

    // init HUD icons
    this.initSurvivalHud()

    // play
    this.play?.addEventListener('click', () => {
      if (this.isTitleScreen) {
        this.onPlay()

        // reset game
        terrain.noise.seed = Math.random()
        terrain.noise.stoneSeed = Math.random()
        terrain.noise.treeSeed = Math.random()
        terrain.noise.coalSeed = Math.random()
        terrain.noise.leafSeed = Math.random()
        terrain.customBlocks = []
        terrain.generateBurgerKingStructure()
        terrain.generateMcDonaldsStructure()
        control.explosions?.clear()
        control.mobManager?.resetToDefaultAnimals()
        terrain.initBlocks()
        terrain.generate()
        terrain.camera.position.x = 8
        terrain.camera.position.z = 8
        terrain.camera.position.y =
          terrain.getElevation(8, 8) + 4
        control.player.setMode(
          control.player.gameMode === GameMode.creative
            ? Mode.flying
            : Mode.walking
        )

        // clear drops on new game
        dropManager.clear()

        // reset survival stats on new game
        if (control.player.gameMode === GameMode.survival) {
          control.player.respawn()
          control.player.inventory.clear()
        }
      }
      this.lockPointer()
    })

    // save load
    this.save?.addEventListener('click', () => {
      if (!this.isTitleScreen) {
        // save game
        const overworldBlocks = terrain.dimension === 'overworld' ? terrain.customBlocks : terrain.overworldCustomBlocks
        const netherBlocks = terrain.dimension === 'nether' ? terrain.customBlocks : terrain.netherCustomBlocks
        window.localStorage.setItem('block', JSON.stringify(overworldBlocks))
        window.localStorage.setItem('block_nether', JSON.stringify(netherBlocks))
        window.localStorage.setItem('dimension', terrain.dimension)
        window.localStorage.setItem('seed', JSON.stringify(terrain.noise.seed))

        window.localStorage.setItem(
          'position',
          JSON.stringify({
            x: terrain.camera.position.x,
            y: terrain.camera.position.y,
            z: terrain.camera.position.z
          })
        )

        // save inventory
        window.localStorage.setItem(
          'inventory',
          control.player.inventory.serialize()
        )

        // save game mode
        window.localStorage.setItem('gameMode', control.player.gameMode)
        window.localStorage.setItem(
          PLAYER_STATS_STORAGE_KEY,
          this.serializePlayerStats(control.player)
        )
        if (control.mobManager) {
          const overworldMobsStr = control.mobManager.serializeAnimalsList(
            terrain.dimension === 'overworld' ? control.mobManager.mobs : control.mobManager.overworldMobs
          )
          const netherMobsStr = control.mobManager.serializeAnimalsList(
            terrain.dimension === 'nether' ? control.mobManager.mobs : control.mobManager.netherMobs
          )
          window.localStorage.setItem('mobs', overworldMobsStr)
          window.localStorage.setItem('mobs_nether', netherMobsStr)
        }

        // ui update
        this.onExit()
        this.onSave()
      } else {
        // load game
        const savedSeed = window.localStorage.getItem('seed')
        const parsedSeed = savedSeed === null ? NaN : Number(savedSeed)
        terrain.noise.seed = Number.isFinite(parsedSeed)
          ? parsedSeed
          : Math.random()

        const savedDimension = window.localStorage.getItem('dimension') || 'overworld'
        terrain.dimension = savedDimension as 'overworld' | 'nether'
        control.core.applyDimensionEnvironment(terrain.dimension)

        const savedBlockData = window.localStorage.getItem('block')
        const savedNetherBlockData = window.localStorage.getItem('block_nether')
        if (savedBlockData === null) {
          terrain.overworldCustomBlocks = []
          terrain.generateBurgerKingStructure()
          terrain.generateMcDonaldsStructure()
        } else {
          terrain.overworldCustomBlocks = JSON.parse(savedBlockData) as Block[]
        }
        if (savedNetherBlockData === null) {
          terrain.netherCustomBlocks = []
        } else {
          terrain.netherCustomBlocks = JSON.parse(savedNetherBlockData) as Block[]
        }

        terrain.customBlocks = terrain.dimension === 'nether' ? terrain.netherCustomBlocks : terrain.overworldCustomBlocks
        terrain.markCustomBlocksDirty()

        control.explosions?.clear()
        terrain.initBlocks()
        terrain.generate()
        const savedMobs = window.localStorage.getItem('mobs')
        const savedNetherMobs = window.localStorage.getItem('mobs_nether')
        if (control.mobManager) {
          control.mobManager.clearMobs()
          control.mobManager.overworldMobs = []
          control.mobManager.netherMobs = []

          if (terrain.dimension === 'overworld') {
            const restored = control.mobManager.deserializeAnimals(savedMobs, control.camera)
            if (!restored && !savedMobs) {
              control.mobManager.resetToDefaultAnimals()
            }
            if (savedNetherMobs) {
              const netherMobs = control.mobManager.deserializeAnimalsToList(savedNetherMobs, control.camera)
              control.mobManager.netherMobs = netherMobs
              for (const mob of netherMobs) {
                control.mobManager.scene.remove(mob.mesh)
              }
            }
          } else {
            control.mobManager.deserializeAnimals(savedNetherMobs, control.camera)
            if (savedMobs) {
              const overworldMobs = control.mobManager.deserializeAnimalsToList(savedMobs, control.camera)
              control.mobManager.overworldMobs = overworldMobs
              for (const mob of overworldMobs) {
                control.mobManager.scene.remove(mob.mesh)
              }
            }
          }
        }

        const position =
          (JSON.parse(window.localStorage.getItem('position') || 'null') as {
            x: number
            y: number
            z: number
          }) ?? null

        position && (terrain.camera.position.x = position.x)
        position && (terrain.camera.position.y = position.y)
        position && (terrain.camera.position.z = position.z)

        // load game mode FIRST — setGameMode(survival) clears the inventory,
        // so the inventory must be deserialized AFTER this runs.
        const savedMode = window.localStorage.getItem('gameMode')
        if (savedMode === GameMode.creative) {
          control.player.setGameMode(GameMode.creative)
          control.player.setMode(Mode.flying)
          this.modeCreative?.classList.add('mode-active')
          this.modeSurvival?.classList.remove('mode-active')
        } else {
          control.player.setGameMode(GameMode.survival)
          this.modeSurvival?.classList.add('mode-active')
          this.modeCreative?.classList.remove('mode-active')
        }

        this.restorePlayerStats(control.player)

        // load inventory (after game mode so it doesn't get wiped by setGameMode)
        const invData = window.localStorage.getItem('inventory')
        if (invData) {
          control.player.inventory.deserialize(invData)
        }

        // clear existing drops
        dropManager.clear()

        // ui update
        this.onPlay()
        this.onLoad()
        this.lockPointer()
      }
    })

    // guide
    this.feature?.addEventListener('click', () => {
      this.features?.classList.remove('hidden')
    })
    this.back?.addEventListener('click', () => {
      this.features?.classList.add('hidden')
    })

    // setting
    this.setting?.addEventListener('click', () => {
      this.settings?.classList.remove('hidden')
    })
    this.settingBack?.addEventListener('click', () => {
      this.settings?.classList.add('hidden')
    })

    // render distance
    this.distanceInput?.addEventListener('input', (e: Event) => {
      if (this.distance && e.target instanceof HTMLInputElement) {
        this.distance.textContent = `Render Distance: ${e.target.value}`
      }
    })

    // fov
    this.fovInput?.addEventListener('input', (e: Event) => {
      if (this.fov && e.target instanceof HTMLInputElement) {
        this.fov.textContent = `Field of View: ${e.target.value}`
        control.camera.fov = parseInt(e.target.value)
        control.camera.updateProjectionMatrix()
      }
    })

    // music
    this.musicInput?.addEventListener('input', (e: Event) => {
      if (this.music && e.target instanceof HTMLInputElement) {
        const disabled = e.target.value === '0'
        control.audio.disabled = disabled
        this.music.textContent = `Music: ${disabled ? 'Off' : 'On'}`
      }
    })

    // music style
    const savedStyle = window.localStorage.getItem('musicStyle')
    if (
      (savedStyle === 'default' || savedStyle === 'phonk') &&
      this.musicStyleInput instanceof HTMLSelectElement
    ) {
      this.musicStyleInput.value = savedStyle
      if (this.musicStyle) {
        this.musicStyle.textContent = `Music Style: ${
          savedStyle === 'phonk' ? 'Phonk' : 'Default'
        }`
      }
    }
    this.musicStyleInput?.addEventListener('change', (e: Event) => {
      if (this.musicStyle && e.target instanceof HTMLSelectElement) {
        const style =
          e.target.value === 'phonk' ? 'phonk' : 'default'
        control.audio.setMusicStyle(style)
        this.musicStyle.textContent = `Music Style: ${
          style === 'phonk' ? 'Phonk' : 'Default'
        }`
      }
    })

    // apply settings
    this.settingBack?.addEventListener('click', () => {
      if (this.distanceInput instanceof HTMLInputElement) {
        terrain.distance = parseInt(this.distanceInput.value)
        terrain.maxCount = terrain.getMaxCount()

        terrain.initBlocks()
        terrain.generate()
        terrain.scene.fog = new THREE.Fog(
          0x87ceeb,
          1,
          terrain.distance * 24 + 24
        )
      }
    })

    // menu, fullscreen, and inventory toggle
    document.body.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.inventoryUI.isOpen) {
        this.inventoryUI.close()
        control.setActive(true)
        document.body.classList.add('game-cursor-hidden')
        this.lockPointer()
        return
      }

      if (
        e.key === 'Escape' &&
        control.active &&
        !document.pointerLockElement &&
        !this.inventoryUI.isOpen
      ) {
        this.onPause()
        return
      }

      if (e.key === 'e' || e.key === 'E') {
        if (this.inventoryUI.isOpen) {
          this.inventoryUI.close()
          control.setActive(true)
          document.body.classList.add('game-cursor-hidden')
          this.lockPointer()
          return
        }

        if (control.active) {
          this.inventoryUI.open('personal')
          control.setActive(false)
          document.body.classList.remove('game-cursor-hidden')
          this.unlockPointer()
          return
        }
      }

      // fullscreen
      if (e.key === 'f') {
        if (document.fullscreenElement) {
          document.exitFullscreen()
        } else {
          document.body.requestFullscreen()
        }
      }
    })

    // exit
    this.exit?.addEventListener('click', () => {
      this.onExit()
    })

    // play / pause handler
    document.addEventListener('pointerlockchange', () => {
      if (document.pointerLockElement) {
        this.onPlay()
      } else {
        // don't show pause menu if inventory is open
        if (!this.inventoryUI.isOpen) {
          this.onPause()
        }
      }
    })

    // disable context menu
    document.addEventListener('contextmenu', e => {
      e.preventDefault()
    })
    document.addEventListener('pointerlockerror', this.onPointerLockError, true)

    // fallback lock handler
    document.querySelector('canvas')?.addEventListener('click', (e: Event) => {
      e.preventDefault()
      if (!this.inventoryUI.isOpen) {
        this.lockPointer()
      }
    })

    document
      .querySelector('canvas')
      ?.addEventListener('pointerdown', this.startFallbackLook)
    document.addEventListener('pointermove', this.updateFallbackLook)
    document.addEventListener('pointerup', this.stopFallbackLook)
  }

  fps: FPS
  bag: Bag
  joystick: Joystick
  terrain: Terrain
  controlRef: Control
  dropManager: BlockDropManager
  inventoryUI: InventoryUI

  menu = document.querySelector('.menu')
  crossHair = document.createElement('div')
  statusChip = document.createElement('div')
  moraleVignette = document.createElement('div')
  fireVignette = document.createElement('div')
  underwaterVignette = document.createElement('div')
  portalVignette = document.createElement('div')
  miningBar = document.createElement('div')
  miningFill = document.createElement('div')

  // buttons
  play = document.querySelector('#play')
  control = document.querySelector('#control')
  setting = document.querySelector('#setting')
  feature = document.querySelector('#feature')
  back = document.querySelector('#back')
  exit = document.querySelector('#exit')
  save = document.querySelector('#save')

  // mode selector
  modeSurvival = document.querySelector('#mode-survival')
  modeCreative = document.querySelector('#mode-creative')
  modeSelector = document.querySelector('.mode-selector')

  // survival elements
  survivalHud = document.querySelector('.survival-hud')
  healthBar = document.querySelector('#health-bar')
  hungerBar = document.querySelector('#hunger-bar')
  airBar = document.querySelector('#air-bar')
  deathScreen = document.querySelector('#death-screen')
  respawnBtn = document.querySelector('#respawn-btn')

  // modals
  saveModal = document.querySelector('.save-modal')
  loadModal = document.querySelector('.load-modal')
  settings = document.querySelector('.settings')
  features = document.querySelector('.features')

  // settings
  distance = document.querySelector('#distance')
  distanceInput = document.querySelector('#distance-input')

  fov = document.querySelector('#fov')
  fovInput = document.querySelector('#fov-input')

  music = document.querySelector('#music')
  musicInput = document.querySelector('#music-input')

  musicStyle = document.querySelector('#music-style')
  musicStyleInput = document.querySelector('#music-style-input')

  settingBack = document.querySelector('#setting-back')

  lastHealth = 20
  lastHunger = 20
  lastAir = -1
  lastStatusText = ''
  lastMoraleOpacity = ''
  lastFireVisible = false
  lastUnderwaterVisible = false
  isTitleScreen = true

  // hotbar refresh throttle
  _hotbarRefreshTimer = 0
  pointerLockBlocked = false
  pointerLockPending = false
  fallbackLookDragging = false
  fallbackLookX = 0
  fallbackLookY = 0
  fallbackLookEuler = new THREE.Euler(0, 0, 0, 'YXZ')
  miningHideTimer = 0

  canUsePointerLock = () => {
    return (
      !isMobile &&
      !this.pointerLockBlocked &&
      typeof document.body.requestPointerLock === 'function'
    )
  }

  onPointerLockError = (event: Event) => {
    if (!this.pointerLockPending && !this.controlRef.active) return

    this.pointerLockBlocked = true
    this.pointerLockPending = false
    event.stopImmediatePropagation()
  }

  lockPointer = () => {
    if (!this.canUsePointerLock()) return
    this.pointerLockPending = true
    try {
      const lockRequest = document.body.requestPointerLock() as
        | Promise<void>
        | void
      if (lockRequest && typeof lockRequest.catch === 'function') {
        lockRequest
          .catch(() => {
            this.pointerLockBlocked = true
          })
          .finally(() => {
            this.pointerLockPending = false
          })
      } else {
        window.setTimeout(() => {
          this.pointerLockPending = false
        }, 250)
      }
    } catch {
      this.pointerLockBlocked = true
      this.pointerLockPending = false
    }
  }

  unlockPointer = () => {
    if (!document.pointerLockElement) return
    document.exitPointerLock()
  }

  startFallbackLook = (event: PointerEvent) => {
    if (
      !this.controlRef.active ||
      this.inventoryUI.isOpen ||
      document.pointerLockElement
    ) {
      return
    }

    this.fallbackLookDragging = true
    this.fallbackLookX = event.clientX
    this.fallbackLookY = event.clientY
  }

  updateFallbackLook = (event: PointerEvent) => {
    if (
      !this.controlRef.active ||
      this.inventoryUI.isOpen ||
      document.pointerLockElement
    ) {
      return
    }

    let dx = event.movementX
    let dy = event.movementY
    if (!dx && !dy) {
      if (
        !this.fallbackLookDragging ||
        (!this.fallbackLookX && !this.fallbackLookY)
      ) {
        this.fallbackLookX = event.clientX
        this.fallbackLookY = event.clientY
        return
      }
      dx = event.clientX - this.fallbackLookX
      dy = event.clientY - this.fallbackLookY
    }
    this.fallbackLookX = event.clientX
    this.fallbackLookY = event.clientY

    this.fallbackLookEuler.setFromQuaternion(this.controlRef.camera.quaternion)
    this.fallbackLookEuler.y -= dx * 0.003
    this.fallbackLookEuler.x -= dy * 0.003
    this.fallbackLookEuler.x = Math.max(
      -Math.PI / 2,
      Math.min(Math.PI / 2, this.fallbackLookEuler.x)
    )
    this.controlRef.camera.quaternion.setFromEuler(this.fallbackLookEuler)
  }

  stopFallbackLook = () => {
    this.fallbackLookDragging = false
  }

  clamp = (value: number, min: number, max: number) => {
    return Math.max(min, Math.min(max, value))
  }

  getSavedStat = (
    value: unknown,
    fallback: number,
    min: number,
    max: number
  ) => {
    const parsed = typeof value === 'number' ? value : Number(value)
    return Number.isFinite(parsed)
      ? this.clamp(parsed, min, max)
      : fallback
  }

  serializePlayerStats = (player: Player) => {
    return JSON.stringify({
      health: player.health,
      hunger: player.hunger,
      morale: Number(player.morale.toFixed(2))
    })
  }

  restorePlayerStats = (player: Player) => {
    if (player.gameMode !== GameMode.survival) return

    const savedStats = window.localStorage.getItem(PLAYER_STATS_STORAGE_KEY)
    if (!savedStats) {
      player.setMorale(player.maxMorale)
      this.lastHealth = player.health
      this.lastHunger = player.hunger
      return
    }

    try {
      const stats = JSON.parse(savedStats) as PlayerStatsSave
      player.health = this.getSavedStat(
        stats.health,
        player.maxHealth,
        0,
        player.maxHealth
      )
      player.hunger = this.getSavedStat(
        stats.hunger,
        player.maxHunger,
        0,
        player.maxHunger
      )
      player.setMorale(
        this.getSavedStat(stats.morale, player.maxMorale, 0, player.maxMorale)
      )
      player.alive = player.health > 0
    } catch {
      player.health = player.maxHealth
      player.hunger = player.maxHunger
      player.setMorale(player.maxMorale)
      player.alive = true
    }

    this.lastHealth = player.health
    this.lastHunger = player.hunger
  }

  getMoraleStatusLine = (player: Player) => {
    return `Morale ${Math.round(player.morale)} | ${player.getMoraleLabel()}`
  }

  setMoraleOpacity = (value: string) => {
    if (this.lastMoraleOpacity === value) return
    this.lastMoraleOpacity = value
    this.moraleVignette.style.opacity = value
  }

  syncMoraleVignette = () => {
    const player = this.controlRef.player
    if (
      !this.controlRef.active ||
      player.gameMode !== GameMode.survival ||
      !player.alive
    ) {
      this.moraleVignette.classList.add('hidden')
      this.setMoraleOpacity('0')
      return
    }

    const pressure = player.morale >= 70 ? 0 : (70 - player.morale) / 70
    this.setMoraleOpacity(
      String(Number((this.clamp(pressure, 0, 1) * 0.38).toFixed(3)))
    )
    this.moraleVignette.classList.remove('hidden')
  }

  syncEffectVignettes = () => {
    const control = this.controlRef
    const player = control.player

    const fireVisible =
      control.active && (player.onFire > 0 || control.isInLava())
    if (fireVisible !== this.lastFireVisible) {
      this.lastFireVisible = fireVisible
      fireVisible
        ? this.fireVignette.classList.remove('hidden')
        : this.fireVignette.classList.add('hidden')
    }

    const underwaterVisible = control.active && control.isUnderwater()
    if (underwaterVisible !== this.lastUnderwaterVisible) {
      this.lastUnderwaterVisible = underwaterVisible
      underwaterVisible
        ? this.underwaterVignette.classList.remove('hidden')
        : this.underwaterVignette.classList.add('hidden')
    }
    const portalDuration = player.gameMode === GameMode.creative ? 0.5 : 5.0
    const portalOpacity = control.portalTimer > 0 ? Math.min(1.0, control.portalTimer / portalDuration) : 0
    if (portalOpacity > 0) {
      this.portalVignette.classList.remove('hidden')
      this.portalVignette.style.opacity = String(portalOpacity)
    } else {
      this.portalVignette.classList.add('hidden')
      this.portalVignette.style.opacity = '0'
    }
  }

  updateMoraleDrift = (delta: number) => {
    const player = this.controlRef.player
    let moralePerSecond = 0

    if (player.hunger <= 0) {
      moralePerSecond -= 0.12
    } else if (player.hunger <= 6) {
      moralePerSecond -= 0.06
    }
    if (player.health < 10) moralePerSecond -= 0.05
    if (this.controlRef.isSwimming()) moralePerSecond -= 0.04
    if (player.hunger >= 16 && player.health >= 16) moralePerSecond += 0.025

    if (moralePerSecond !== 0) {
      player.adjustMorale(moralePerSecond * delta)
    }
  }

  initSurvivalHud = () => {
    if (this.healthBar) {
      this.healthBar.innerHTML = ''
      for (let i = 0; i < 10; i++) {
        const heart = document.createElement('div')
        heart.className = 'heart full'
        this.healthBar.appendChild(heart)
      }
    }
    if (this.hungerBar) {
      this.hungerBar.innerHTML = ''
      for (let i = 0; i < 10; i++) {
        const drumstick = document.createElement('div')
        drumstick.className = 'drumstick full'
        this.hungerBar.appendChild(drumstick)
      }
    }
    if (this.airBar) {
      this.airBar.innerHTML = ''
      for (let i = 0; i < 10; i++) {
        const bubble = document.createElement('div')
        bubble.className = 'bubble full'
        this.airBar.appendChild(bubble)
      }
      this.airBar.classList.add('hidden')
    }
  }

  updateHealthBar = (player: Player) => {
    if (!this.healthBar) return
    const hearts = this.healthBar.children
    for (let i = 0; i < 10; i++) {
      const heartValue = player.health - i * 2
      if (heartValue >= 2) {
        hearts[i].className = 'heart full'
      } else if (heartValue >= 1) {
        hearts[i].className = 'heart half'
      } else {
        hearts[i].className = 'heart'
      }
    }
  }

  updateHungerBar = (player: Player) => {
    if (!this.hungerBar) return
    const drumsticks = this.hungerBar.children
    for (let i = 0; i < 10; i++) {
      const hungerValue = player.hunger - i * 2
      if (hungerValue >= 2) {
        drumsticks[i].className = 'drumstick full'
      } else if (hungerValue >= 1) {
        drumsticks[i].className = 'drumstick half'
      } else {
        drumsticks[i].className = 'drumstick'
      }
    }
  }

  updateAirBar = (player: Player) => {
    if (!this.airBar) return
    if (player.air >= player.maxAir) {
      this.airBar.classList.add('hidden')
      return
    }

    this.airBar.classList.remove('hidden')
    const bubbles = this.airBar.children
    for (let i = 0; i < 10; i++) {
      const bubbleValue = (player.air / player.maxAir) * 10 - i
      if (bubbleValue >= 1) {
        bubbles[i].className = 'bubble full'
      } else if (bubbleValue >= 0.5) {
        bubbles[i].className = 'bubble half'
      } else {
        bubbles[i].className = 'bubble'
      }
    }
  }

  syncAirBar = (player: Player) => {
    // -1 means full air (bar hidden); otherwise air in half-bubble units
    const airState =
      player.air >= player.maxAir
        ? -1
        : Math.max(0, Math.floor((player.air / player.maxAir) * 20))
    if (airState === this.lastAir) return
    this.lastAir = airState
    this.updateAirBar(player)
  }

  showDamageFlash = () => {
    const flash = document.createElement('div')
    flash.className = 'damage-flash'
    document.body.appendChild(flash)
    setTimeout(() => flash.remove(), 300)
  }

  updateMiningProgress = (progress: number) => {
    window.clearTimeout(this.miningHideTimer)

    if (progress <= 0) {
      this.miningBar.classList.add('hidden')
      this.miningFill.style.width = '0%'
      return
    }

    this.miningBar.classList.remove('hidden')
    this.miningFill.style.width = `${Math.min(1, progress) * 100}%`

    if (progress >= 1) {
      this.miningHideTimer = window.setTimeout(() => {
        this.miningBar.classList.add('hidden')
        this.miningFill.style.width = '0%'
      }, 140)
    }
  }

  onPlay = () => {
    this.isTitleScreen = false
    this.controlRef.setActive(true)
    document.body.classList.add('game-cursor-hidden')
    isMobile && this.joystick.init()
    this.menu?.classList.add('hidden')
    this.menu?.classList.remove('start')
    this.play && (this.play.textContent = RESUME_LABEL)
    this.crossHair.classList.remove('hidden')
    this.feature?.classList.add('hidden')
    this.settings?.classList.add('hidden')
    this.features?.classList.add('hidden')
    this.deathScreen?.classList.add('hidden')
    this.bag.bag.classList.remove('hidden')
    this.fps.fps.classList.remove('hidden')
    this.statusChip.classList.remove('hidden')
    this._lastTick = performance.now()
    this.syncStatusChip()
    this.syncMoraleVignette()
    this.syncEffectVignettes()

    // show/hide survival HUD
    const player = this.controlRef.player
    if (player.gameMode === GameMode.survival && player.alive) {
      this.survivalHud?.classList.remove('hidden')
      this.updateHealthBar(player)
      this.updateHungerBar(player)
      this.syncAirBar(player)
    } else {
      this.survivalHud?.classList.add('hidden')
    }

    // refresh hotbar
    this.bag.refresh()
  }

  onPause = () => {
    this.controlRef.setActive(false)
    document.body.classList.remove('game-cursor-hidden')
    this.menu?.classList.remove('hidden')
    this.crossHair.classList.add('hidden')
    this.save && (this.save.textContent = SAVE_EXIT_LABEL)
    this.statusChip.classList.add('hidden')
    this.moraleVignette.classList.add('hidden')
    this.setMoraleOpacity('0')
    this.syncEffectVignettes()
    this.bag.bag.classList.add('hidden')
    this.fps.fps.classList.add('hidden')
    this._lastTick = performance.now()
  }

  onExit = () => {
    this.isTitleScreen = true
    this.controlRef.setActive(false)
    document.body.classList.remove('game-cursor-hidden')
    this.menu?.classList.add('start')
    this.play && (this.play.textContent = PLAY_LABEL)
    this.save && (this.save.textContent = LOAD_LABEL)
    this.feature?.classList.remove('hidden')
    this.survivalHud?.classList.add('hidden')
    this.crossHair.classList.add('hidden')
    this.deathScreen?.classList.add('hidden')
    this.settings?.classList.add('hidden')
    this.features?.classList.add('hidden')
    this.statusChip.classList.add('hidden')
    this.moraleVignette.classList.add('hidden')
    this.setMoraleOpacity('0')
    this.syncEffectVignettes()
    this.bag.bag.classList.add('hidden')
    this.fps.fps.classList.add('hidden')
    this._lastTick = performance.now()
  }

  syncStatusChip = () => {
    const player = this.controlRef.player
    let text: string
    if (player.gameMode === GameMode.creative) {
      text =
        player.mode === Mode.flying
          ? 'Creative | Flight'
          : this.controlRef.isSwimming()
            ? 'Creative | Swim'
            : 'Creative | Walk'
    } else {
      text = this.controlRef.isSwimming()
        ? `Survival | Swimming\n${this.getMoraleStatusLine(player)}`
        : `Survival\n${this.getMoraleStatusLine(player)}`
    }
    if (text !== this.lastStatusText) {
      this.lastStatusText = text
      this.statusChip.textContent = text
    }
  }

  onSave = () => {
    this.saveModal?.classList.remove('hidden')
    setTimeout(() => {
      this.saveModal?.classList.add('show')
    })
    setTimeout(() => {
      this.saveModal?.classList.remove('show')
    }, 1000)

    setTimeout(() => {
      this.saveModal?.classList.add('hidden')
    }, 1350)
  }

  onLoad = () => {
    this.loadModal?.classList.remove('hidden')
    setTimeout(() => {
      this.loadModal?.classList.add('show')
    })
    setTimeout(() => {
      this.loadModal?.classList.remove('show')
    }, 1000)

    setTimeout(() => {
      this.loadModal?.classList.add('hidden')
    }, 1350)
  }

  update = () => {
    this.fps.update()

    // refresh hotbar periodically (every ~5 frames worth of time)
    const now = performance.now()
    if (now - this._hotbarRefreshTimer > 100) {
      this._hotbarRefreshTimer = now
      this.bag.refresh()

      // also refresh inventory UI if open
      if (this.inventoryUI.isOpen) {
        this.inventoryUI.refresh()
      }
    }

    const player = this.controlRef.player
    this.syncStatusChip()
    this.syncMoraleVignette()
    this.syncEffectVignettes()

    if (player.gameMode === GameMode.survival && !player.alive) {
      this.deathScreen?.classList.remove('hidden')
      this.survivalHud?.classList.add('hidden')
      this.crossHair.classList.add('hidden')
      this.moraleVignette.classList.add('hidden')
      this.setMoraleOpacity('0')
      this.controlRef.setActive(false)
      document.body.classList.remove('game-cursor-hidden')
      this.unlockPointer()
      return
    }

    if (player.gameMode !== GameMode.survival) return
    if (!this.controlRef.active) return

    const delta = (now - (this._lastTick || now)) / 1000
    this._lastTick = now

    // tick down spawn protection
    if (player.spawnProtection > 0) {
      player.spawnProtection = Math.max(0, player.spawnProtection - delta)
    }

    this.updateMoraleDrift(delta)

    // hunger depletion: morale pressure shortens the survival timer.
    player.hungerTimer += delta
    if (
      player.hungerTimer >=
      player.getMoraleHungerInterval(BASE_HUNGER_INTERVAL_SECONDS)
    ) {
      player.hungerTimer = 0
      player.depleteHunger(1)
    }

    // health regen when hunger >= 18 (9+ drumsticks): 1 hp every 2 seconds
    if (player.hunger >= 18) {
      player.regenTimer += delta
      if (player.regenTimer >= 2) {
        player.regenTimer = 0
        player.heal(1)
      }
    } else {
      player.regenTimer = 0
    }

    // starvation damage when hunger === 0: 1 hp every 4 seconds
    if (player.hunger <= 0) {
      player.starveTimer += delta
      if (player.starveTimer >= 4) {
        player.starveTimer = 0
        player.takeDamage(1)
        this.showDamageFlash()
      }
    } else {
      player.starveTimer = 0
    }

    // update HUD only when values change
    if (player.health !== this.lastHealth) {
      this.updateHealthBar(player)
      this.lastHealth = player.health
    }
    if (player.hunger !== this.lastHunger) {
      this.updateHungerBar(player)
      this.lastHunger = player.hunger
    }
    this.syncAirBar(player)

    // death check
    if (!player.alive) {
      this.deathScreen?.classList.remove('hidden')
      this.survivalHud?.classList.add('hidden')
      this.crossHair.classList.add('hidden')
      this.moraleVignette.classList.add('hidden')
      this.setMoraleOpacity('0')
      document.body.classList.remove('game-cursor-hidden')
      this.unlockPointer()
    }
  }

  _lastTick = 0
}
